import { useEffect, useState } from 'react';
import { Save, ShoppingBag } from 'lucide-react';
import { getInventarioCfg, putInventarioCfg, avisarVitrina, VitrinaInventarioCfg } from '../../finances/vitrinaApi';
import { useToast } from '../../../context/ToastContext';

/** Mi Estudio → Inventario de la vitrina. Dos interruptores (ajuste `vitrina_inventory`):
 *  el inventario en sí y el candado que no deja cerrar la caja sin conteo. Los dos nacen apagados. */
export default function VitrinaInventarioCard() {
  const toast = useToast();
  const [cfg, setCfg] = useState<VitrinaInventarioCfg | null>(null);
  const [orig, setOrig] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getInventarioCfg().then((c) => { setCfg(c); setOrig(JSON.stringify(c)); }).catch(() => setCfg(null));
  }, []);
  if (!cfg) return null;   // sin permiso sobre la vitrina (o módulo apagado): la tarjeta no sale

  const dirty = JSON.stringify(cfg) !== orig;
  async function guardar() {
    if (!cfg) return;
    setSaving(true);
    try {
      const limpio = { enabled: cfg.enabled, require_count_on_close: cfg.enabled && cfg.require_count_on_close };
      await putInventarioCfg(limpio);
      setCfg(limpio); setOrig(JSON.stringify(limpio));
      avisarVitrina();
      toast.success(limpio.enabled ? 'Inventario de vitrina encendido' : 'Inventario de vitrina apagado');
    } catch (e: any) { toast.error(e?.message || 'No se pudo guardar'); }
    finally { setSaving(false); }
  }

  return (
    <section className="rounded-2xl border border-line bg-surface p-5 shadow-sm" data-testid="mi-estudio-vitrina">
      <h2 className="flex items-center gap-2 font-display text-base font-bold text-content"><ShoppingBag size={17} className="text-brand-blue" /> Inventario de la vitrina</h2>
      <p className="mb-4 mt-0.5 text-xs text-content-3">
        Encendido, cada venta y cada fiado descuentan existencias, y al abrir el turno de caja se pide contar la vitrina.
      </p>
      <label className="flex items-start gap-2.5 text-sm text-content">
        <input type="checkbox" checked={cfg.enabled} data-testid="inv-enabled"
          onChange={(e) => setCfg({ enabled: e.target.checked, require_count_on_close: e.target.checked && cfg.require_count_on_close })}
          className="mt-0.5 h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
        <span>
          Llevar inventario de la vitrina
          <span className="block text-xs text-content-3">Conviene encenderlo justo antes de un conteo: las existencias de hoy pueden no ser reales.</span>
        </span>
      </label>
      <label className={`mt-3 flex items-start gap-2.5 text-sm ${cfg.enabled ? 'text-content' : 'text-content-3'}`}>
        <input type="checkbox" disabled={!cfg.enabled} checked={cfg.enabled && cfg.require_count_on_close} data-testid="inv-candado"
          onChange={(e) => setCfg({ ...cfg, require_count_on_close: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[rgb(var(--c-accent-solid))]" />
        <span>
          No dejar cerrar la caja sin conteo
          <span className="block text-xs text-content-3">Apagado, un turno sin contar solo se avisa en pantalla y en el corte de WhatsApp.</span>
        </span>
      </label>
      <button onClick={guardar} disabled={saving || !dirty}
        className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-accent-solid px-3.5 py-2 text-sm font-semibold text-on-accent transition disabled:bg-line disabled:text-content-3">
        <Save size={15} /> {saving ? 'Guardando…' : dirty ? 'Guardar cambios' : 'Sin cambios'}
      </button>
    </section>
  );
}
